import { useState } from 'react';

interface StructuredSummary {
  background_social?: string;
  background_academic?: string;
  background_personal?: string;
  purpose_what?: string;
  purpose_how?: string;
  purpose_scope?: string;
  intervention_group?: string;
  control_group?: string;
  significance_academic?: string;
  significance_social?: string;
  significance_practical?: string;
}

interface ResearchProcedure {
  title: string;
  steps: Array<{
    title: string;
    description: string;
    order: number;
  }>;
  updated_at: string;
}

interface ResearchOverviewProps {
  overview: {
    id?: string;
    user_id?: string;
    title?: string;
    theme?: string;
    summary?: string;
    rq?: string;
    hypotheses?: string[];
    progress_metrics?: any;
    next_month_goal?: string;
    created_at?: string;
    updated_at?: string;
  } | null;
  lastUpdated: string;
  isAdmin?: boolean;
  currentUser?: any;
  onEdit?: (item: any) => void;
}

/**
 * progress_metrics から構造化サマリーを取得する（配列・オブジェクト両対応）
 */
function extractStructuredFromMetrics(metrics: any): StructuredSummary | null {
  if (!metrics) return null;
  if (Array.isArray(metrics)) {
    const found = metrics.find((m: any) => m.type === 'structured_summary');
    if (found?.data && typeof found.data === 'object') {
      return found.data as StructuredSummary;
    }
  } else if (typeof metrics === 'object') {
    if (metrics.structured_summary?.data) {
      return metrics.structured_summary.data as StructuredSummary;
    }
  }
  return null;
}

/**
 * summary フィールドから構造化データをパース（JSON形式のみ）
 */
function parseSummaryJSON(raw: string | null | undefined): StructuredSummary | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as StructuredSummary;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * 日付フォーマット
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * 連続する句点「。。」を「。」に正規化し、文末の余分な句点も除去する
 */
function cleanText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/。{2,}/g, '。')   // 「。。」「。。。」→「。」
    .replace(/。\s*。/g, '。')   // 「。 。」→「。」
    .trim();
}

/**
 * テキストをそのまま表示（句点の追加・除去は一切しない）
 */
function InfoCard({
  icon,
  label,
  text,
  bgColor = 'bg-white',
}: {
  icon: string;
  label: string;
  text: string;
  bgColor?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  const cleanedText = cleanText(text);

  if (!cleanedText || !cleanedText.trim()) return null;

  const shouldTruncate = cleanedText.length > 150;
  const displayText = shouldTruncate && !expanded ? cleanedText.slice(0, 150) + '...' : cleanedText;

  return (
    <div className={`${bgColor} dark:bg-gray-700/60 rounded-lg p-4 border border-gray-100 dark:border-gray-600`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 flex items-center justify-center bg-teal-100 dark:bg-teal-900/50 rounded">
          <i className={`${icon} text-teal-600 dark:text-teal-400 text-xs`}></i>
        </div>
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{displayText}</p>
      {shouldTruncate && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium flex items-center gap-1 cursor-pointer"
        >
          {expanded ? (
            <><i className="ri-arrow-up-s-line"></i>閉じる</>
          ) : (
            <><i className="ri-arrow-down-s-line"></i>続きを読む</>
          )}
        </button>
      )}
    </div>
  );
}

function SectionBlock({
  title,
  icon,
  color,
  children,
}: {
  title: string;
  icon: string;
  color: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-6 sm:px-8 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 flex items-center justify-center ${color} rounded-lg`}>
            <i className={`${icon} text-sm`}></i>
          </div>
          <h3 className="text-sm font-bold text-gray-800 dark:text-white">{title}</h3>
        </div>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-gray-400 dark:text-gray-500 text-lg`}></i>
      </button>
      {open && <div className="px-6 sm:px-8 pb-5">{children}</div>}
    </div>
  );
}

export default function ResearchOverview({
  overview,
  lastUpdated,
  isAdmin = false,
  currentUser,
  onEdit,
}: ResearchOverviewProps) {
  if (!overview) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-file-text-line text-teal-600 dark:text-teal-400"></i>
          </div>
          研究概要
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">研究概要がまだ登録されていません。</p>
      </div>
    );
  }

  // ★ progress_metrics → summary JSON の順で構造化データを取得
  const metricsStructured = extractStructuredFromMetrics(overview.progress_metrics);
  const summaryStructured = parseSummaryJSON(overview.summary);
  const structured: StructuredSummary = metricsStructured || summaryStructured || {};

  // 少なくとも1つのフィールドに値があれば構造化表示
  const hasStructuredData = Object.values(structured).some(v => v && String(v).trim().length > 0);

  // 研究手順データの取得
  let researchProcedure: ResearchProcedure | null = null;
  if (overview.progress_metrics && !Array.isArray(overview.progress_metrics) && typeof overview.progress_metrics === 'object') {
    researchProcedure = overview.progress_metrics?.research_procedure || null;
  }

  // 既存の連結テキスト（JSON以外のsummary）をフォールバック表示用に保持
  const plainSummary = !hasStructuredData && overview.summary && !overview.summary.startsWith('{')
    ? overview.summary
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* ヘッダー */}
      <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-600 px-6 sm:px-8 py-6 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg">
                <i className="ri-file-text-line text-lg"></i>
              </div>
              <span className="text-teal-100 text-xs font-medium uppercase tracking-wider">テーマと目的</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold mb-2">
              {overview.title ?? '（タイトルなし）'}
            </h2>
            <p className="text-teal-100 text-sm">最終更新: {formatDate(lastUpdated)}</p>
          </div>
          {(isAdmin || (currentUser && overview.user_id === currentUser.id)) && onEdit && (
            <button
              onClick={() => onEdit(overview)}
              className="w-9 h-9 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-lg transition-colors cursor-pointer flex-shrink-0"
              title="編集"
            >
              <i className="ri-edit-line text-lg"></i>
            </button>
          )}
        </div>
      </div>

      {/* 構造化データ表示 */}
      {hasStructuredData ? (
        <>
          {/* 背景 */}
          {(structured.background_social || structured.background_academic || structured.background_personal) && (
            <SectionBlock title="背景" icon="ri-earth-line" color="bg-teal-100 text-teal-600 dark:bg-teal-900/50 dark:text-teal-400">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {structured.background_social && (
                  <InfoCard icon="ri-global-line" label="社会的背景" text={structured.background_social} />
                )}
                {structured.background_academic && (
                  <InfoCard icon="ri-book-2-line" label="学術的背景" text={structured.background_academic} />
                )}
                {structured.background_personal && (
                  <InfoCard icon="ri-user-heart-line" label="個人的動機" text={structured.background_personal} bgColor="bg-gray-50" />
                )}
              </div>
            </SectionBlock>
          )}

          {/* 目的 */}
          {(structured.purpose_what || structured.purpose_how || structured.purpose_scope || structured.intervention_group || structured.control_group) && (
            <SectionBlock title="目的" icon="ri-focus-3-line" color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400">
              <div className="space-y-3">
                {structured.purpose_what && (
                  <InfoCard icon="ri-crosshair-2-line" label="何を明らかにするか" text={structured.purpose_what} />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {structured.purpose_how && (
                    <InfoCard icon="ri-tools-line" label="どうやって明らかにするか" text={structured.purpose_how} bgColor="bg-gray-50" />
                  )}
                  {structured.purpose_scope && (
                    <InfoCard icon="ri-ruler-line" label="対象範囲" text={structured.purpose_scope} bgColor="bg-gray-50" />
                  )}
                </div>
                {(structured.intervention_group || structured.control_group) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {structured.intervention_group && (
                      <InfoCard icon="ri-flask-line" label="介入群" text={structured.intervention_group} bgColor="bg-emerald-50" />
                    )}
                    {structured.control_group && (
                      <InfoCard icon="ri-shield-check-line" label="対照群" text={structured.control_group} bgColor="bg-emerald-50" />
                    )}
                  </div>
                )}
              </div>
            </SectionBlock>
          )}

          {/* 意義 */}
          {(structured.significance_academic || structured.significance_social || structured.significance_practical) && (
            <SectionBlock title="意義" icon="ri-award-line" color="bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {structured.significance_academic && (
                  <InfoCard icon="ri-graduation-cap-line" label="学術的意義" text={structured.significance_academic} />
                )}
                {structured.significance_social && (
                  <InfoCard icon="ri-government-line" label="社会的意義" text={structured.significance_social} />
                )}
                {structured.significance_practical && (
                  <InfoCard icon="ri-briefcase-line" label="実務的意義" text={structured.significance_practical} bgColor="bg-gray-50" />
                )}
              </div>
            </SectionBlock>
          )}
        </>
      ) : plainSummary ? (
        /* 旧形式の連結テキストをそのまま表示（句点の追加なし） */
        <div className="px-6 sm:px-8 py-6 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">概要</h3>
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{plainSummary}</p>
        </div>
      ) : null}

      {/* リサーチクエスチョン */}
      {overview.rq && (
        <div className="px-6 sm:px-8 py-5 border-b border-gray-100 dark:border-gray-700 bg-amber-50/40 dark:bg-amber-900/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-amber-100 dark:bg-amber-900/50 rounded-lg">
              <i className="ri-question-answer-line text-amber-600 dark:text-amber-400 text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-gray-800 dark:text-white">リサーチクエスチョン</h3>
          </div>
          <p className="text-gray-900 dark:text-gray-200 font-medium text-sm leading-relaxed pl-9">{cleanText(overview.rq)}</p>
        </div>
      )}

      {/* 仮説 */}
      {Array.isArray(overview.hypotheses) && overview.hypotheses.length > 0 && (
        <div className="px-6 sm:px-8 py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-100 dark:bg-teal-900/50 rounded-lg">
              <i className="ri-lightbulb-flash-line text-teal-600 dark:text-teal-400 text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-gray-800 dark:text-white">仮説</h3>
          </div>
          <div className="space-y-2.5 pl-9">
            {overview.hypotheses.map((hypothesis: string, index: number) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full flex items-center justify-center font-semibold text-xs mt-0.5">
                  {index + 1}
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed flex-1">{cleanText(hypothesis)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 研究手順 */}
      {researchProcedure && (
        <SectionBlock
          title="研究手順"
          icon="ri-route-line"
          color="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400"
        >
          <div className="space-y-4">
            <div className="bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800">
              <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-1">{researchProcedure.title}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">最終更新: {formatDate(researchProcedure.updated_at)}</p>
            </div>
            <div className="space-y-3">
              {researchProcedure.steps.map((step, index) => (
                <div key={index} className="bg-white dark:bg-gray-700/60 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-full flex items-center justify-center font-bold text-sm">
                      {step.order}
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">{step.title}</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SectionBlock>
      )}

      {/* 進捗メトリクス（配列形式のみ） */}
      {Array.isArray(overview.progress_metrics) && (() => {
        const progressItems = overview.progress_metrics.filter(
          (m: any) => m.type !== 'structured_summary' && m.name && m.target !== undefined
        );
        if (progressItems.length === 0) return null;
        return (
          <div className="px-6 sm:px-8 py-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/20">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-100 dark:bg-teal-900/50 rounded-lg">
                <i className="ri-bar-chart-box-line text-teal-600 dark:text-teal-400 text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-gray-800 dark:text-white">進捗状況</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-9">
              {progressItems.map((metric: any, index: number) => {
                const progress = metric.target > 0 ? (metric.current / metric.target) * 100 : 0;
                const safeProgress = Math.min(Math.max(progress, 0), 100);
                return (
                  <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-3.5 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{metric.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{metric.current} / {metric.target}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-teal-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${safeProgress}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* 次の目標 */}
      {overview.next_month_goal && (
        <div className="px-6 sm:px-8 py-5 bg-amber-50/60 dark:bg-amber-900/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 flex items-center justify-center bg-amber-100 dark:bg-amber-900/50 rounded-lg">
              <i className="ri-flag-line text-amber-600 dark:text-amber-400 text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-gray-800 dark:text-white">次の1ヶ月の最重要目標</h3>
          </div>
          <p className="text-gray-800 dark:text-gray-200 font-medium text-sm leading-relaxed pl-9">{cleanText(overview.next_month_goal)}</p>
        </div>
      )}
    </div>
  );
}
